// Two full courses, each 10 lessons.
//  Course 1 "foundations": Beginner -> Intermediate (lessons 1-10)
//  Course 2 "mastery":     Intermediate -> Advanced (lessons 11-20)
// Lesson ids are globally unique so per-lesson progress works across courses.
//
// Section staff: { heading, text, clef, notes:[{pitch|pitches[], label, duration, color}], sequence?:[pitches] }
// Quiz question: { id, type:'note-id'|'mc', prompt, clef?, pitch?, options[], answer }

const MASTERY_LESSONS = [
  {
    id: 11,
    title: "Compound Time Signatures",
    level: "Intermediate",
    topic: "6/8, 9/8 & 12/8 — the dotted-quarter beat",
    minutes: 11,
    summary:
      "In compound time the beat divides into three, not two. 6/8 has two beats, each a dotted quarter; 9/8 has three; 12/8 has four. Count '1-2-3, 4-5-6' with the accent on 1 and 4.",
    sections: [
      {
        heading: "Feeling 6/8 in two",
        text:
          "6/8 groups six eighth notes into two pulses of three. Say 'ONE-and-a, TWO-and-a'. Click through one bar of 6/8 to feel the lilting, rolling motion.",
        clef: "treble",
        notes: [
          { pitch: "C4", label: "1", duration: "eighth" },
          { pitch: "D4", label: "&", duration: "eighth" },
          { pitch: "E4", label: "a", duration: "eighth" },
          { pitch: "F4", label: "2", duration: "eighth" },
          { pitch: "G4", label: "&", duration: "eighth" },
          { pitch: "A4", label: "a", duration: "eighth" },
        ],
        sequence: ["C4", "D4", "E4", "F4", "G4", "A4"],
      },
    ],
    tips: ["Compound = the beat splits into 3. Simple time (4/4, 3/4) splits into 2.", "In 6/8 the two strong beats fall on eighth-notes 1 and 4."],
    quiz: [
      { id: "11a", type: "mc", prompt: "In compound time, each beat divides into…", options: ["Two", "Three", "Four", "Five"], answer: "Three" },
      { id: "11b", type: "mc", prompt: "How many main beats does 6/8 have?", options: ["2", "3", "6", "8"], answer: "2" },
      { id: "11c", type: "mc", prompt: "Which note value gets one beat in 6/8?", options: ["Quarter", "Eighth", "Dotted quarter", "Half"], answer: "Dotted quarter" },
      { id: "11d", type: "mc", prompt: "9/8 time has how many dotted-quarter beats?", options: ["2", "3", "4", "9"], answer: "3" },
    ],
  },
  {
    id: 12,
    title: "Minor Keys & Scales",
    level: "Intermediate",
    topic: "Natural, harmonic & melodic minor",
    minutes: 12,
    summary:
      "Every major key has a relative minor sharing its key signature. Minor comes in three flavours: natural, harmonic (raised 7th) and melodic (raised 6th & 7th going up).",
    sections: [
      {
        heading: "A natural minor",
        text:
          "A minor is the relative minor of C major — same notes, no sharps or flats, but starting on A. It sounds darker and more serious. Click through the natural minor scale.",
        clef: "treble",
        notes: [
          { pitch: "A4", label: "A", duration: "quarter" },
          { pitch: "B4", label: "B", duration: "quarter" },
          { pitch: "C5", label: "C", duration: "quarter" },
          { pitch: "D5", label: "D", duration: "quarter" },
          { pitch: "E5", label: "E", duration: "quarter" },
          { pitch: "F5", label: "F", duration: "quarter" },
          { pitch: "G5", label: "G", duration: "quarter" },
          { pitch: "A5", label: "A", duration: "quarter" },
        ],
        sequence: ["A4", "B4", "C5", "D5", "E5", "F5", "G5", "A5"],
      },
      {
        heading: "A harmonic minor (raised 7th)",
        text:
          "Harmonic minor raises the 7th degree — here G becomes G♯ — creating that exotic, tense pull back to the tonic. Listen to the difference.",
        clef: "treble",
        notes: [
          { pitch: "A4", label: "A", duration: "quarter" },
          { pitch: "B4", label: "B", duration: "quarter" },
          { pitch: "C5", label: "C", duration: "quarter" },
          { pitch: "D5", label: "D", duration: "quarter" },
          { pitch: "E5", label: "E", duration: "quarter" },
          { pitch: "F5", label: "F", duration: "quarter" },
          { pitch: "G#5", label: "G♯", duration: "quarter", color: "#B45309" },
          { pitch: "A5", label: "A", duration: "quarter" },
        ],
        sequence: ["A4", "B4", "C5", "D5", "E5", "F5", "G#5", "A5"],
      },
    ],
    tips: ["Relative minor is a minor 3rd (3 half steps) below the major tonic: C major ↔ A minor.", "Harmonic minor raises 7; melodic minor raises 6 and 7 ascending, natural descending."],
    quiz: [
      { id: "12a", type: "mc", prompt: "The relative minor of C major is…", options: ["A minor", "E minor", "G minor", "F minor"], answer: "A minor" },
      { id: "12b", type: "mc", prompt: "Harmonic minor raises which scale degree?", options: ["2nd", "4th", "6th", "7th"], answer: "7th" },
      { id: "12c", type: "mc", prompt: "A relative minor sits how far below its major?", options: ["A whole step", "A minor 3rd", "A perfect 4th", "A tritone"], answer: "A minor 3rd" },
      { id: "12d", type: "mc", prompt: "Melodic minor (ascending) raises which two degrees?", options: ["6 and 7", "2 and 3", "4 and 5", "1 and 8"], answer: "6 and 7" },
    ],
  },
  {
    id: 13,
    title: "Intervals by Sight & Sound",
    level: "Intermediate",
    topic: "2nds through the octave",
    minutes: 12,
    summary:
      "An interval is the distance between two notes. Reading intervals lets you jump around the staff instantly. Learn the look and sound of 2nds, 3rds, 5ths and octaves.",
    sections: [
      {
        heading: "Steps vs. skips",
        text:
          "A 2nd is line-to-space (a step). A 3rd is line-to-line or space-to-space (a skip). A 5th spans three lines. Click each interval to hear both notes together.",
        clef: "treble",
        notes: [
          { pitches: ["C4", "D4"], label: "2nd", duration: "half" },
          { pitches: ["C4", "E4"], label: "3rd", duration: "half" },
          { pitches: ["C4", "G4"], label: "5th", duration: "half" },
          { pitches: ["C4", "C5"], label: "octave", duration: "half" },
        ],
      },
    ],
    tips: ["Count both notes: C up to G is C-D-E-F-G = a 5th.", "3rds stack to build every chord — train your eye to spot them fast."],
    quiz: [
      { id: "13a", type: "mc", prompt: "The distance from C up to E is a…", options: ["2nd", "3rd", "4th", "5th"], answer: "3rd" },
      { id: "13b", type: "mc", prompt: "A line-to-adjacent-space distance is a…", options: ["2nd", "3rd", "5th", "octave"], answer: "2nd" },
      { id: "13c", type: "mc", prompt: "C up to C (8 letters) is an…", options: ["5th", "6th", "7th", "octave"], answer: "octave" },
      { id: "13d", type: "mc", prompt: "From C up to G is a perfect…", options: ["4th", "5th", "6th", "7th"], answer: "5th" },
    ],
  },
  {
    id: 14,
    title: "Triads & Chord Qualities",
    level: "Advanced",
    topic: "Major, minor, diminished & augmented",
    minutes: 13,
    summary:
      "A triad is three notes stacked in thirds: root, third and fifth. The quality — major, minor, diminished or augmented — depends on the size of those thirds.",
    sections: [
      {
        heading: "Four qualities on C",
        text:
          "All four triads share the root C but differ in the middle and top notes. Major = bright, minor = sad, diminished = tense, augmented = dreamy. Click each stack to hear the quality.",
        clef: "treble",
        notes: [
          { pitches: ["C4", "E4", "G4"], label: "C major", duration: "half" },
          { pitches: ["C4", "Eb4", "G4"], label: "C minor", duration: "half", color: "#B45309" },
          { pitches: ["C4", "Eb4", "Gb4"], label: "C dim", duration: "half", color: "#B45309" },
          { pitches: ["C4", "E4", "G#4"], label: "C aug", duration: "half", color: "#B45309" },
        ],
      },
    ],
    tips: ["Major triad = major 3rd + minor 3rd. Minor triad = minor 3rd + major 3rd.", "Read chords from the bottom up: root, then stack thirds."],
    quiz: [
      { id: "14a", type: "mc", prompt: "A triad is built by stacking…", options: ["2nds", "3rds", "4ths", "5ths"], answer: "3rds" },
      { id: "14b", type: "mc", prompt: "A major triad on C contains C, E and…", options: ["G", "G♯", "G♭", "A"], answer: "G" },
      { id: "14c", type: "mc", prompt: "Lowering the 3rd of a major triad makes it…", options: ["Augmented", "Minor", "Diminished", "Dominant"], answer: "Minor" },
      { id: "14d", type: "mc", prompt: "A diminished triad has a lowered 3rd AND a lowered…", options: ["Root", "5th", "7th", "octave"], answer: "5th" },
    ],
  },
  {
    id: 15,
    title: "Seventh Chords & Inversions",
    level: "Advanced",
    topic: "Dominant, major & minor 7ths + inversions",
    minutes: 13,
    summary:
      "Add one more third on top of a triad and you get a seventh chord — the colour behind jazz and classical harmony. Inversions rearrange which chord tone sits in the bass.",
    sections: [
      {
        heading: "Three sevenths on G and C",
        text:
          "The dominant 7th (G7) drives strongly to C. Major 7th is lush; minor 7th is smooth. Click each four-note stack to hear its flavour.",
        clef: "treble",
        notes: [
          { pitches: ["G3", "B3", "D4", "F4"], label: "G7 (dom)", duration: "half", color: "#B45309" },
          { pitches: ["C4", "E4", "G4", "B4"], label: "Cmaj7", duration: "half" },
          { pitches: ["D4", "F4", "A4", "C5"], label: "Dm7", duration: "half", color: "#B45309" },
        ],
      },
      {
        heading: "Inversion: the same chord, new bass",
        text:
          "First inversion puts the 3rd in the bass. Here is C major with E on the bottom — same three notes, different colour and voice-leading.",
        clef: "treble",
        notes: [
          { pitches: ["C4", "E4", "G4"], label: "root", duration: "half" },
          { pitches: ["E4", "G4", "C5"], label: "1st inv", duration: "half", color: "#B45309" },
          { pitches: ["G4", "C5", "E5"], label: "2nd inv", duration: "half", color: "#B45309" },
        ],
      },
    ],
    tips: ["A dominant 7th = major triad + minor 7th; it wants to resolve down a 5th.", "Inversions are named by which chord tone is in the bass: root, 3rd, 5th, 7th."],
    quiz: [
      { id: "15a", type: "mc", prompt: "A seventh chord has how many notes?", options: ["Two", "Three", "Four", "Five"], answer: "Four" },
      { id: "15b", type: "mc", prompt: "A dominant 7th chord most wants to resolve…", options: ["Up a step", "Down a 5th", "To itself", "Nowhere"], answer: "Down a 5th" },
      { id: "15c", type: "mc", prompt: "First inversion places which chord tone in the bass?", options: ["Root", "3rd", "5th", "7th"], answer: "3rd" },
      { id: "15d", type: "mc", prompt: "Cmaj7 is spelled C-E-G plus which note?", options: ["A", "B♭", "B", "C"], answer: "B" },
    ],
  },
  {
    id: 16,
    title: "Advanced Rhythm",
    level: "Advanced",
    topic: "Triplets, tuplets & cross-rhythm",
    minutes: 12,
    summary:
      "Tuplets squeeze an 'unexpected' number of notes into a beat. A triplet fits three even notes where two would normally go. Mastering them frees your rhythm completely.",
    sections: [
      {
        heading: "The eighth-note triplet",
        text:
          "Three eighth-note triplets fill one quarter-note beat: count 'tri-pl-et' or '1-and-a'. Click the group to hear three even notes inside a single beat.",
        clef: "treble",
        notes: [
          { pitch: "E4", label: "tri", duration: "eighth" },
          { pitch: "G4", label: "pl", duration: "eighth" },
          { pitch: "C5", label: "et", duration: "eighth" },
          { pitch: "E4", label: "tri", duration: "eighth" },
          { pitch: "G4", label: "pl", duration: "eighth" },
          { pitch: "C5", label: "et", duration: "eighth" },
        ],
        sequence: ["E4", "G4", "C5", "E4", "G4", "C5"],
      },
    ],
    tips: ["A triplet bracket with a '3' means 3 notes in the time of 2.", "Practice 2-against-3 slowly: say 'not-diffi-cult' to lock the cross-rhythm."],
    quiz: [
      { id: "16a", type: "mc", prompt: "An eighth-note triplet fills the time of…", options: ["One eighth", "One quarter", "One half", "One whole"], answer: "One quarter" },
      { id: "16b", type: "mc", prompt: "The bracket over a triplet shows which number?", options: ["2", "3", "4", "6"], answer: "3" },
      { id: "16c", type: "mc", prompt: "'Two against three' is an example of…", options: ["A key change", "A cross-rhythm", "A cadence", "An inversion"], answer: "A cross-rhythm" },
      { id: "16d", type: "mc", prompt: "A tuplet lets you fit an unusual number of notes into…", options: ["A whole piece", "One beat or grouping", "A key signature", "A clef"], answer: "One beat or grouping" },
    ],
  },
  {
    id: 17,
    title: "The Church Modes",
    level: "Advanced",
    topic: "Dorian, Phrygian, Lydian, Mixolydian & more",
    minutes: 12,
    summary:
      "Modes are scales built on each degree of the major scale. They give melodies distinct colours — Dorian's cool minor, Lydian's floating brightness, Mixolydian's bluesy edge.",
    sections: [
      {
        heading: "D Dorian",
        text:
          "Play the white keys from D to D and you get Dorian — a minor mode with a bright raised 6th (B natural). It sounds folky and cool. Click through it.",
        clef: "treble",
        notes: [
          { pitch: "D4", label: "D", duration: "quarter" },
          { pitch: "E4", label: "E", duration: "quarter" },
          { pitch: "F4", label: "F", duration: "quarter" },
          { pitch: "G4", label: "G", duration: "quarter" },
          { pitch: "A4", label: "A", duration: "quarter" },
          { pitch: "B4", label: "B", duration: "quarter" },
          { pitch: "C5", label: "C", duration: "quarter" },
          { pitch: "D5", label: "D", duration: "quarter" },
        ],
        sequence: ["D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5"],
      },
    ],
    tips: ["All white-key modes: C Ionian, D Dorian, E Phrygian, F Lydian, G Mixolydian, A Aeolian, B Locrian.", "Ionian = major; Aeolian = natural minor. The rest sit between."],
    quiz: [
      { id: "17a", type: "mc", prompt: "Which mode is the same as the major scale?", options: ["Dorian", "Ionian", "Aeolian", "Locrian"], answer: "Ionian" },
      { id: "17b", type: "mc", prompt: "Which mode equals the natural minor scale?", options: ["Lydian", "Mixolydian", "Aeolian", "Phrygian"], answer: "Aeolian" },
      { id: "17c", type: "mc", prompt: "Dorian is built on which degree of the major scale?", options: ["1st", "2nd", "4th", "5th"], answer: "2nd" },
      { id: "17d", type: "mc", prompt: "Mixolydian is famous for its lowered…", options: ["2nd", "3rd", "6th", "7th"], answer: "7th" },
    ],
  },
  {
    id: 18,
    title: "Chromaticism & Modulation",
    level: "Advanced",
    topic: "Passing tones, key changes & secondary dominants",
    minutes: 13,
    summary:
      "Chromatic notes lie outside the key and add colour and tension. Modulation moves the music into a new key entirely — often via a pivot chord or a secondary dominant.",
    sections: [
      {
        heading: "A chromatic run",
        text:
          "Chromatic passing tones fill the gaps between scale notes with half steps. Click through this rising chromatic line and hear the tension build.",
        clef: "treble",
        notes: [
          { pitch: "C4", label: "C", duration: "eighth" },
          { pitch: "C#4", label: "C♯", duration: "eighth", color: "#B45309" },
          { pitch: "D4", label: "D", duration: "eighth" },
          { pitch: "D#4", label: "D♯", duration: "eighth", color: "#B45309" },
          { pitch: "E4", label: "E", duration: "eighth" },
          { pitch: "F4", label: "F", duration: "eighth" },
        ],
        sequence: ["C4", "C#4", "D4", "D#4", "E4", "F4"],
      },
    ],
    tips: ["A secondary dominant (like V/V) tonicises a chord other than the home key.", "Watch for a run of accidentals — it often signals a modulation is coming."],
    quiz: [
      { id: "18a", type: "mc", prompt: "A chromatic note is one that…", options: ["Belongs to the key", "Lies outside the key", "Is always the tonic", "Has no pitch"], answer: "Lies outside the key" },
      { id: "18b", type: "mc", prompt: "Modulation means…", options: ["Getting louder", "Changing key", "Slowing down", "Adding a rest"], answer: "Changing key" },
      { id: "18c", type: "mc", prompt: "A chromatic scale moves entirely in…", options: ["Whole steps", "Half steps", "3rds", "5ths"], answer: "Half steps" },
      { id: "18d", type: "mc", prompt: "A 'V of V' chord is an example of a…", options: ["Pivot rest", "Secondary dominant", "Passing clef", "Time signature"], answer: "Secondary dominant" },
    ],
  },
  {
    id: 19,
    title: "Ornaments, Articulation & Dynamics",
    level: "Advanced",
    topic: "Trills, staccato, accents & expression",
    minutes: 11,
    summary:
      "Notation tells you not just what to play but how. Articulation (staccato, legato, accent), ornaments (trills, mordents) and dynamics (p, f, cresc.) bring a score to life.",
    sections: [
      {
        heading: "Same notes, different touch",
        text:
          "Imagine this phrase played smooth and connected (legato), then short and detached (staccato), then with a strong accent. The pitches are identical — the expression is everything. Click to hear the phrase.",
        clef: "treble",
        notes: [
          { pitch: "G4", label: "legato", duration: "quarter" },
          { pitch: "A4", label: "staccato", duration: "quarter" },
          { pitch: "B4", label: "accent", duration: "quarter" },
          { pitch: "C5", label: "tenuto", duration: "quarter" },
        ],
        sequence: ["G4", "A4", "B4", "C5"],
      },
    ],
    tips: ["Dynamics: pp < p < mp < mf < f < ff. 'cresc.' = get louder, 'dim.' = get softer.", "A trill rapidly alternates the written note with the one above it."],
    quiz: [
      { id: "19a", type: "mc", prompt: "Staccato means the note is played…", options: ["Smoothly", "Short and detached", "Very loud", "Twice"], answer: "Short and detached" },
      { id: "19b", type: "mc", prompt: "Which dynamic is the softest?", options: ["f", "mp", "pp", "mf"], answer: "pp" },
      { id: "19c", type: "mc", prompt: "A trill alternates the written note with the note…", options: ["Below it", "Above it", "An octave away", "A 5th away"], answer: "Above it" },
      { id: "19d", type: "mc", prompt: "'Crescendo' tells you to gradually…", options: ["Get louder", "Get softer", "Speed up", "Slow down"], answer: "Get louder" },
    ],
  },
  {
    id: 20,
    title: "Advanced Sight-Reading & Transposition",
    level: "Advanced",
    topic: "Full scores, transposing instruments & mastery",
    minutes: 14,
    summary:
      "The finish line: read a full score fluently, understand transposing instruments, and combine every skill — clef, key, rhythm, harmony and expression — at first sight.",
    sections: [
      {
        heading: "A grand-staff phrase",
        text:
          "Reading a grand staff means tracking treble and bass together. Click through this treble line, then the bass line, and imagine them sounding at once.",
        clef: "treble",
        notes: [
          { pitches: ["C4", "E4", "G4"], label: "I", duration: "half" },
          { pitches: ["D4", "F4", "A4"], label: "ii", duration: "half", color: "#B45309" },
          { pitches: ["G3", "B3", "D4"], label: "V", duration: "half", color: "#B45309" },
          { pitches: ["C4", "E4", "G4"], label: "I", duration: "half" },
        ],
      },
      {
        heading: "Bass foundation",
        text:
          "The same progression, rooted in the bass clef. Great sight-readers hear the harmony from the bass up.",
        clef: "bass",
        notes: [
          { pitch: "C3", label: "I", duration: "half" },
          { pitch: "D3", label: "ii", duration: "half", color: "#B45309" },
          { pitch: "G2", label: "V", duration: "half", color: "#B45309" },
          { pitch: "C3", label: "I", duration: "half" },
        ],
        sequence: ["C3", "D3", "G2", "C3"],
      },
    ],
    tips: ["A transposing instrument (like B♭ trumpet) sounds a different pitch than written — know the interval.", "Sight-read every day: fluency comes from mileage, not talent."],
    quiz: [
      { id: "20a", type: "mc", prompt: "A grand staff combines which two clefs?", options: ["Treble & alto", "Treble & bass", "Bass & tenor", "Alto & tenor"], answer: "Treble & bass" },
      { id: "20b", type: "mc", prompt: "A B♭ transposing instrument plays a written C and it sounds as…", options: ["C", "B♭", "D", "G"], answer: "B♭" },
      { id: "20c", type: "note-id", prompt: "Name this bass-clef note:", clef: "bass", pitch: "C3", options: ["A", "C", "E", "G"], answer: "C" },
      { id: "20d", type: "mc", prompt: "The single best way to improve sight-reading is to…", options: ["Memorise one piece", "Practice reading new music daily", "Play only fast", "Avoid rhythm"], answer: "Practice reading new music daily" },
    ],
  },
];

import {
  DRUMMER_FOUNDATION, DRUMMER_MASTERY,
  GUITAR_MASTERY, PIANO_MASTERY,
} from "./instruments";
import { FOUNDATION_THEORY, MASTERY_THEORY } from "./theoryGrades";
import { NOTATION_THEORY, NOTATION_QUIZ } from "./notationExtras";
import { startingCourse, DRUM_THEORY_OPENING, drumTheoryQuiz, drumBandTheory, masteryExtras, gospelExtras } from "./startingPoint";

const GROOVE_AT = {
  25: { heading: "Groove Builder: toms and crash", grooveBuilder: 2, text: "Now you can read eighth-note grooves, build your own with more of the kit: start a groove with a crash on beat 1, and try the toms. Press Play, then write your favourite on your drum score paper." },
  27: { heading: "Groove Builder: two-bar grooves with fills", grooveBuilder: 3, text: "Build a two-bar groove: keep time on the ride in bar 1, then add a fill around the toms at the end of bar 2 and land on a crash. Press Play and listen to how the fill leads back to the start." },
};
const DRUM_START = DRUMMER_FOUNDATION.map((l, idx) => (l.id === 21
  ? { ...l, sections: [...DRUM_THEORY_OPENING, ...l.sections], quiz: [...l.quiz, ...drumTheoryQuiz] }
  : { ...l, sections: [...l.sections, ...(GROOVE_AT[l.id] ? [GROOVE_AT[l.id]] : []), ...drumBandTheory(idx)] }));
import { enrichLesson } from "./extras";
import { GOSPEL_PIANO, GOSPEL_GUITAR, GOSPEL_PIANO_BEGINNER, GOSPEL_GUITAR_BEGINNER } from "./gospel";
import { lessonTrack, PLAY_MINUTES } from "./playalong";

const FOUNDATION_BLURB =
  "Start from absolute zero. By the end of these 10 lessons you'll confidently read music at an intermediate standard.";
const MASTERY_BLURB =
  "For readers who already know the basics. Ten lessons take you from intermediate to an advanced standard.";

function makeCourses(instrumentId, foundation, mastery, foundationBlurb, masteryBlurb) {
  const e = enrichLesson(instrumentId);
  return [
    { id: "foundations", order: 1, title: "Foundations of Reading Music", tagline: "Beginner → Intermediate", from: "Beginner", to: "Intermediate", blurb: foundationBlurb || FOUNDATION_BLURB, lessons: foundation.map(e) },
    { id: "mastery", order: 2, title: "Advanced Sight-Reading & Theory", tagline: "Intermediate → Advanced", from: "Intermediate", to: "Advanced", blurb: masteryBlurb || MASTERY_BLURB, lessons: mastery.map(masteryExtras(instrumentId)).map(e) },
  ];
}

export const INSTRUMENTS = [
  {
    id: "singers", label: "Singers", icon: "mic",
    tagline: "Sight-singing & vocal reading",
    courses: makeCourses(
      "singers", startingCourse("singers", 0), MASTERY_LESSONS,
      "Learn to read music as a singer, from zero, with the stave shown side by side with the keyboard and the words you sing underneath, so you learn how they work together. You'll cover the stave and clefs (treble and bass), letter names, Middle C, finding your range and knowing your voice part, crotchets to quavers, rests and ties, accidentals, major scales with sol-fa, triads and harmony — with full theory and musical terms — reaching an intermediate standard.",
      "Compound time, minor keys, intervals, chords, modes and full-score reading take your sight-singing to an advanced level."
    ),
  },
  {
    id: "drummers", label: "Drummers", icon: "drum",
    tagline: "Rhythm & drum-kit notation",
    courses: makeCourses(
      "drummers", DRUM_START, DRUMMER_MASTERY,
      "Learn to read drum score and the stave side by side, from zero: how each line and space of the stave becomes a drum or cymbal, the drum kit and drum key, note values and grooves — from your first rock beat to reading a full groove chart — plus the same full music theory as every JT Hub course: clefs, letter names, time signatures, accidentals, major scales and keys, triads and musical terms, reaching an intermediate standard.",
      "Linear grooves, rudiments, odd meters, double bass, polyrhythms and full chart sight-reading — with advanced theory: every key, intervals, chords, cadences, irregular time and musical terms — to an advanced standard."
    ),
  },
  {
    id: "guitarists", label: "Guitarists", icon: "guitar",
    tagline: "Notation, TAB & chords",
    courses: makeCourses(
      "guitarists", startingCourse("guitarists", 40), GUITAR_MASTERY,
      "Learn to read guitar music on both the stave and TAB, from zero. As a guitarist you should be able to read notes off a stave and on TAB, so every lesson shows both side by side and explains the differences. You'll cover the stave and clefs, how TAB lines and fret numbers match the notes on the stave, letter names on the stave, keyboard and fretboard, Middle C on the guitar, note values, rests and ties, the bass clef, accidentals, five-note finger practice, major scales in different positions and keys, triads and arpeggios, chord symbols and chord names, Drop D tuning and playing the same chord in different positions on the neck — and all the theory works for bass guitarists too — up to an intermediate standard.",
      "Position playing, barre chords, higher positions, arpeggios, CAGED and full score reading to an advanced level."
    ),
  },
  {
    id: "pianists", label: "Pianists", icon: "piano",
    tagline: "Grand-staff & two-hand reading",
    courses: makeCourses(
      "pianists", startingCourse("pianists", 60), PIANO_MASTERY,
      "Start from zero: the stave and clefs, letter names, note values, rests and ties, the bass clef, accidentals, five-finger practice, major scales with both hands and fingering, triads, block and broken chords and arpeggios — with full theory and musical terms — reaching an intermediate standard.",
      "Inversions, seventh chords, wide ranges, fingering, lead sheets, voice leading and full-score reading to an advanced level."
    ),
  },
];

const gospelCourse = (instrumentId, id, level, title, blurb, lessons) => ({
  id, order: id === "gospel" ? 4 : 3, mini: true, price: GOSPEL_PRICE, title, level, tagline: `Gospel ${level} course`, from: level === "Beginner" ? "Beginner" : "Intermediate", to: `Gospel ${level}`,
  blurb, lessons: lessons.map(gospelExtras(instrumentId)).map((l) => ({ ...enrichLesson(instrumentId)(l), minutes: l.minutes })),
});
export const GOSPEL_PRICE = 300;
export const GOSPEL_PAIR_PRICE = 500;
INSTRUMENTS.find((i) => i.id === "pianists").miniCourses = [
  gospelCourse("pianists", "gospel-beginner", "Beginner", "Gospel Piano — Beginner",
    "Six lessons: gospel by numbers and the Cycle of Fifths, major and minor pentatonic scales, slash chords and passing notes, the blues scale and blues licks, passing chords, and gospel licks — because improvising doesn't mean guessing.", GOSPEL_PIANO_BEGINNER),
  gospelCourse("pianists", "gospel", "Intermediate", "Gospel Piano — Intermediate",
    "Six lessons: upper extensions round the Cycle of Fifths, rootless voicings, drop 2 chords, diminished scales and gospel diminished movements, modes and alternate scales, and improvising with intention.", GOSPEL_PIANO)];
INSTRUMENTS.find((i) => i.id === "guitarists").miniCourses = [
  gospelCourse("guitarists", "gospel-beginner", "Beginner", "Gospel Guitar — Beginner",
    "Six lessons: gospel by numbers and the Cycle of Fifths, major and minor pentatonic, slash chords and passing notes, the blues scale and blues licks, passing chords, and gospel licks — because improvising doesn't mean guessing.", GOSPEL_GUITAR_BEGINNER),
  gospelCourse("guitarists", "gospel", "Intermediate", "Gospel Guitar — Intermediate",
    "Six lessons: upper extensions round the Cycle of Fifths, top-string triads and drop 2 chords, diminished scales and movements, alternate scales, modes and when to use them, and improvising with intention.", GOSPEL_GUITAR)];
export const GOSPEL_COURSES = INSTRUMENTS.flatMap((inst) => (inst.miniCourses || []).map((c) => ({ key: `${inst.id}:${c.id}`, instrument_id: inst.id, course_id: c.id, title: c.title, level: c.level })));
const allCourses = (inst) => [...inst.courses, ...(inst.miniCourses || [])];

const earlierIn = (inst, course, idx) => [
  ...(course.id === "mastery" ? inst.courses.find((c) => c.id === "foundations").lessons : []),
  ...course.lessons.slice(0, idx),
];

INSTRUMENTS.forEach((inst) => inst.courses.forEach((course) => course.lessons.forEach((l, idx) => {
  const extra = [...((course.id === "foundations" ? FOUNDATION_THEORY : MASTERY_THEORY)[idx] || []), ...(course.id === "mastery" ? NOTATION_THEORY[idx] || [] : [])];
  l.sections = [...l.sections, ...extra];
  if (course.id === "mastery" && NOTATION_QUIZ[idx]) l.quiz = [...l.quiz, ...NOTATION_QUIZ[idx](l.id)];
  l.minutes += extra.length * 4;
})));

// Lesson plan: recap + warm-up quiz + core lesson + play-along, 20–30 minutes.
INSTRUMENTS.forEach((inst) => allCourses(inst).forEach((course) => course.lessons.forEach((l, idx) => {
  const earlier = earlierIn(inst, course, idx);
  const recap = 2, warm = earlier.length ? 3 : 0, play = lessonTrack(l.id) ? PLAY_MINUTES : 0;
  const base = recap + warm + play;
  const total = Math.min(30, Math.max(20, base + Math.max(15, l.minutes)));
  l.plan = { recap, warm, core: total - base, play };
  l.minutes = total;
})));

export function lessonRecapContext(id) {
  const ctx = getLessonContext(id);
  if (!ctx) return { prev: null, earlier: [] };
  const idx = ctx.number - 1;
  const earlier = earlierIn(ctx.instrument, ctx.course, idx);
  return { prev: earlier[earlier.length - 1] || null, earlier, instrument: ctx.instrument, course: ctx.course };
}

export const ALL_LESSONS = INSTRUMENTS.flatMap((inst) => allCourses(inst).flatMap((c) => c.lessons));

export function getInstrument(instrumentId) {
  return INSTRUMENTS.find((i) => i.id === instrumentId);
}

export function getCourse(instrumentId, courseId) {
  const inst = getInstrument(instrumentId);
  return inst ? allCourses(inst).find((c) => c.id === courseId) : null;
}

export function getLesson(id) {
  return ALL_LESSONS.find((l) => l.id === Number(id));
}

// Returns { instrument, course, number } for a lesson id.
export function getLessonContext(id) {
  const n = Number(id);
  for (const inst of INSTRUMENTS) {
    for (const course of allCourses(inst)) {
      const idx = course.lessons.findIndex((l) => l.id === n);
      if (idx >= 0) return { instrument: inst, course, number: idx + 1 };
    }
  }
  return null;
}

export function getLessonCourse(id) {
  const ctx = getLessonContext(id);
  return ctx ? ctx.course : null;
}

// Pick the lesson to resume: prefer the next uncompleted lesson inside the
// course the learner last opened, otherwise the earliest uncompleted overall.
export function pickContinueLesson(completedIds = [], lastLessonId = null) {
  const done = new Set(completedIds);
  if (lastLessonId != null) {
    const ctx = getLessonContext(lastLessonId);
    if (ctx) {
      const inCourse = ctx.course.lessons.find((l) => !done.has(l.id));
      if (inCourse) return inCourse;
    }
  }
  return ALL_LESSONS.find((l) => !done.has(l.id)) || null;
}

// Courses a learner has fully completed (all lessons) -> certificate list.
export function earnedCertificates(completedIds = []) {
  const done = new Set((completedIds || []).map(Number));
  const out = [];
  for (const inst of INSTRUMENTS) {
    for (const course of allCourses(inst)) {
      if (course.lessons.length && course.lessons.every((l) => done.has(l.id))) {
        out.push({ instrument_id: inst.id, course_id: course.id, title: `${inst.label} — ${course.title}`, tagline: course.tagline });
      }
    }
  }
  return out;
}

export function nextLessonInCourse(id) {
  const ctx = getLessonContext(id);
  if (!ctx) return null;
  const idx = ctx.course.lessons.findIndex((l) => l.id === Number(id));
  return idx >= 0 && idx + 1 < ctx.course.lessons.length ? ctx.course.lessons[idx + 1] : null;
}

// Course access helpers (payment gating). No free previews — every lesson
// requires the course to be purchased.
export function courseKeyOf(id) {
  const ctx = getLessonContext(id);
  return ctx ? `${ctx.instrument.id}:${ctx.course.id}` : null;
}

export function isFreeLesson(id) {
  return false;
}

export function lessonUnlocked(id, purchases = []) {
  if (isFreeLesson(id)) return true;
  const key = courseKeyOf(id);
  return purchases.includes(key);
}

// Flat list of every course with its instrument, for the pricing page.
export const INSTRUMENT_ART = {
  singers: "https://images.unsplash.com/photo-1522863602463-afebb8886ab2?crop=entropy&cs=srgb&fm=jpg&q=85",
  drummers: "https://images.unsplash.com/photo-1503506584117-5ed92741077a?crop=entropy&cs=srgb&fm=jpg&q=85",
  guitarists: "https://images.unsplash.com/photo-1588450523206-e0b048d8f4d3?crop=entropy&cs=srgb&fm=jpg&q=85",
  pianists: "/media/jt-grand-piano.jpg",
};

export const ALL_COURSES = INSTRUMENTS.flatMap((inst) =>
  inst.courses.map((c) => ({
    instrument_id: inst.id,
    instrument_label: inst.label,
    course_id: c.id,
    title: `${inst.label} — ${c.title}`,
    tagline: c.tagline,
    key: `${inst.id}:${c.id}`,
  }))
);

// Standalone trainer pool for the Sight-Reading Trainer page.
export const TRAINER_POOL = {
  treble: ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5"],
  bass: ["E2", "F2", "G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3"],
};
